import React from 'react';
import { CheckCircle, Package, MapPin, Calendar, ArrowRight, Home, ShoppingBag } from 'lucide-react';
import { useI18n } from '../i18n';

const OrderSuccessPage = ({ orderId, onNavigate }) => {
  const { t } = useI18n();
  const estimatedDelivery = new Date();
  estimatedDelivery.setDate(estimatedDelivery.getDate() + 4);
  
  const formatDate = (date) => {
    return date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-md mx-auto px-4 pt-12 pb-8">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('orderSuccess', 'orderCompleted')}</h1>
          <p className="text-gray-600">{t('orderSuccess', 'thankYou')}</p>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
            <div>
              <p className="text-sm text-gray-500">{t('orderSuccess', 'orderNumber')}</p>
              <p className="font-bold text-gray-900">{orderId || 'ORD123456'}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-emerald-600" />
            </div>
          </div>

          {/* Delivery Info */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderSuccess', 'expectedDelivery')}</p>
                <p className="font-medium text-gray-900">{formatDate(estimatedDelivery)}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderSuccess', 'shippingAddress')}</p>
                <p className="font-medium text-gray-900">Via Roma 123, 20100 Milano</p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-900 mb-4">{t('orderSuccess', 'orderStatus')}</p>
            <div className="space-y-4">
              {[
                { status: t('orderSuccess', 'orderConfirmed'), time: t('orderSuccess', 'now'), done: true },
                { status: t('orderSuccess', 'inPreparation'), time: `${t('orderSuccess', 'within')} 24h`, done: false },
                { status: t('orderSuccess', 'shipped'), time: `2-3 ${t('orderSuccess', 'days')}`, done: false },
                { status: t('orderSuccess', 'delivered'), time: formatDate(estimatedDelivery), done: false }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-4 h-4 rounded-full ${step.done ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      {step.done && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    {i < 3 && <div className={`w-0.5 h-8 ${step.done ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
                  </div>
                  <div className="-mt-1">
                    <p className={`font-medium ${step.done ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {step.status}
                    </p>
                    <p className="text-sm text-gray-400">{step.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Email notification */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800">
            {t('orderSuccess', 'emailConfirmation')}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button 
            onClick={() => onNavigate('orders')}
            className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-5 h-5" />
            {t('orderSuccess', 'myOrders')}
          </button>
          <button 
            onClick={() => onNavigate('search-results')}
            className="w-full bg-white text-gray-700 py-4 rounded-xl font-semibold border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            {t('orderSuccess', 'continueShopping')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
